import { z } from 'zod';

export const TagNeedsReview = 'NeedsReview';

export const userRole = z.enum(['admin', 'standard']);
export type UserRole = z.infer<typeof userRole>;

export const user = z.object({
    id: z.number(),
    email: z.string(),
    name: z.string(),
    role: userRole,
    disabled: z.boolean(),
    lastLoginAt: z.nullable(z.coerce.date()).optional(),
    createdAt: z.coerce.date(),
});
export type User = z.infer<typeof user>;

export const tokenResponse = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    user: user,
});
export type TokenResponse = z.infer<typeof tokenResponse>;

export const listUsersResponse = z.object({
    users: z.array(user),
});
export type ListUsersResponse = z.infer<typeof listUsersResponse>;

export interface LoginRequest {
    email: string;
    password: string;
}

export interface CreateUserRequest {
    email: string;
    name: string;
    role: UserRole;
}

export interface UpdateUserRequest {
    name?: string;
    role?: UserRole;
    disabled?: boolean;
}

export interface ChangePasswordRequest {
    currentPassword: string;
    newPassword: string;
}

export const parkingRevenueStatsSummary = z.object({
    provider: z.string(),
    revenue: z.number(),
    revenueOriginalCurrency: z.number(),
});

export type ParkingRevenueStatsSummary = z.infer<typeof parkingRevenueStatsSummary>;

export const parkingRevenueStats = z.object({
    revenue: z.number(),
    visitors: z.number(),
    clicks: z.number(),
    provider: z.string(),
    revenueOriginalCurrency: z.number(),
});

export type ParkingRevenueStats = z.infer<typeof parkingRevenueStats>;

export const parkingRevenueStatsByDate = z.object({
    date: z.coerce.date(),
    stats: z.array(parkingRevenueStats)
});

export type ParkingRevenueStatsByDate = z.infer<typeof parkingRevenueStatsByDate>;

export const listingStatus = z.enum(["listed", "sold", "needsTransfer", "payoutPending", "complete", "cancelled"]);
export type ListingStatus = z.infer<typeof listingStatus>;

export const transferType = z.enum(['regular', 'fast']);
export const payoutType = z.enum(['full', 'installment']);
export type TransferType = z.infer<typeof transferType>;
export type PayoutType = z.infer<typeof payoutType>;

export const listing = z.object({
    id: z.number(),
    domain: z.string(),
    account: z.string(),
    venue: z.string(),
    active: z.boolean(),
    status: listingStatus,
    createdAt: z.coerce.date(),
    listedAt: z.nullable(z.coerce.date()),
    soldAt: z.nullable(z.coerce.date()),
    payoutDate: z.nullable(z.coerce.date()),
    transferType: z.nullable(transferType),
    payoutType: z.nullable(payoutType),
    listPrice: z.nullable(z.number()),
    salePrice: z.nullable(z.number()),
    payoutAmount: z.nullable(z.number()),
    installmentsTotal: z.nullable(z.number()),
    installmentsPaid: z.nullable(z.number()),
});
export type Listing = z.infer<typeof listing>;


export const portfolioRenewalStats = z.object({
    manual: z.number(),
    legacyPicks: z.number(),
    autoPicks: z.number(),
    other: z.number(),
    autoRenew: z.number(),
    methodAutoRenew: z.number(),
    methodOther: z.number(),
    methodSupportEmail: z.number(),
    methodLegacy: z.number(),
    total: z.number(),
});
export type PortfolioRenewalStats = z.infer<typeof portfolioRenewalStats>;

export const portfolioStats = z.object({
    deletedTotal: z.number(),
    totalDomains: z.number(),
    domainsAdded: z.number(),
    additions: portfolioRenewalStats,
    existing: portfolioRenewalStats,
});
export type PortfolioStats = z.infer<typeof portfolioStats>;

export const portfolioStatsByDate = z.object({
    date: z.coerce.date(),
    stats: portfolioStats,
});

export type PortfolioStatsByDate = z.infer<typeof portfolioStatsByDate>;

export const eventsSummary = z.object({
    domainAdded: z.optional(z.number()),
    domainRenewed: z.optional(z.number()),
    domainDeleted: z.optional(z.number()),
});
export type EventsSummary = z.infer<typeof eventsSummary>;

export const autopickingRevenue = z.object({
    name: z.string(),
    parkingRevenueUsd: z.number()
});

export type AutopickingRevenue = z.infer<typeof autopickingRevenue>;

export const metricsStatsDaily = z.object({
    date: z.coerce.date(),
    ahrefsDr: z.number(),
    extensionsTaken: z.number(),
    whoisCreate: z.number(),
    estibot: z.number(),
    parkingRevenue: z.number(),
    goscore: z.number(),
    totalDomains: z.number(),
});
export type MetricsStatsDaily = z.infer<typeof metricsStatsDaily>;

export const autopickingRule = z.object({
    id: z.number(),
    name: z.string(),
    rule: z.string(),
    createdAt: z.coerce.date(),
    enabled: z.boolean(),
    parkingRevenueUsd: z.number(),
    totalDomains: z.number(),
});

export type AutopickingRule = z.infer<typeof autopickingRule>;

export const listRulesResponse = z.object({
    rules: z.array(autopickingRule),
});

export type ListRulesResponse = z.infer<typeof listRulesResponse>;

export const listingsStats = z.object({
    venue: z.string(),
    account: z.string(),
    numSales: z.number(),
    numPayouts: z.number(),
    revenueUSD: z.number(),
    recurringRevenueUSD: z.number(),
});
export type ListingsStats = z.infer<typeof listingsStats>;

export const listingsStatsByDate = z.object({
    date: z.coerce.date(),
    stats: z.array(listingsStats),
});
export type ListingsStatsByDate = z.infer<typeof listingsStatsByDate>;

export const listingActivityType = z.enum(['sold', 'transferComplete', 'payout', 'cancelled', 'transferRequest']);
export type ListingActivityType = z.infer<typeof listingActivityType>;

export const listingActivity = z.object({
    domain: z.string(),
    account: z.string(),
    type: listingActivityType,
    createdAt: z.coerce.date(),
    source: z.string(),
    data: z.string(),
});
export type ListingActivity = z.infer<typeof listingActivity>;

export const ncAftermarketAuctionsStats = z.object({
    date: z.coerce.date(),
    totalSales: z.number(),
    totalSalesPortfolio: z.number(),
    totalRevenueUSD: z.number(),
    totalRevenueUSDPortfolio: z.number(),
});
export type NCAftermarketAuctionsStats = z.infer<typeof ncAftermarketAuctionsStats>;

export const autorenewStatsDetail = z.object({
    autorenewEnabled: z.number(),
    autorenewDisabled: z.number(),
});
export type AutorenewStatsDetail = z.infer<typeof autorenewStatsDetail>;

export const autorenewStats = z.object({
    stats: z.record(z.string(), autorenewStatsDetail),
});
export type AutorenewStats = z.infer<typeof autorenewStats>;

export const expiringThisMonthStatsDetail = z.object({
    date: z.coerce.date(),
    total: z.number(),
    autorenewEnabled: z.number(),
    autorenewDisabled: z.number(),
});
export type ExpiringThisMonthStatsDetail = z.infer<typeof expiringThisMonthStatsDetail>;

export const expiringThisMonthStats = z.object({
    stats: z.record(z.string(), z.array(expiringThisMonthStatsDetail)),
});
export type ExpiringThisMonthStats = z.infer<typeof expiringThisMonthStats>;

export const portfolioAccountStatsByDate = z.object({
    date: z.coerce.date(),
    totalDomainsRenewed: z.number(),
    totalRenewalFeesPaid: z.number(),
    totalAutorenewEnabled: z.number(),
    totalAutorenewDisabled: z.number(),
    totalDomainsSystemAutorenewEnabled: z.number().nullable().optional(),
    totalDomainsSystemAutorenewDisabled: z.number().nullable().optional(),
});
export type PortfolioAccountStatsByDate = z.infer<typeof portfolioAccountStatsByDate>;

export const portfolioAccountStats = z.object({
    stats: z.record(z.string(), z.array(portfolioAccountStatsByDate)),
});
export type PortfolioAccountStats = z.infer<typeof portfolioAccountStats>;

export const ltoInstallmentsSummary = z.object({
    activeLTOCount: z.number(),
    totalRemainingAmount: z.number(),
    totalInstallmentsRemaining: z.number(),
    estimatedNextMonthAmount: z.number(),
    estimatedNextMonthCount: z.number(),
    ltoSoldCount: z.number(),
    defaultedCount: z.number(),
    defaultedAmount: z.number(),
    defaultRate: z.number(),
    estimatedDefaultedCount: z.number(),
    estimatedDefaultedAmount: z.number(),
});
export type LTOInstallmentsSummary = z.infer<typeof ltoInstallmentsSummary>;

export const ltoDefault = z.object({
    domain: z.string(),
    account: z.string(),
    source: z.string(),
    createdAt: z.coerce.date(),
    soldAt: z.coerce.date(),
    soldAtDate: z.string(),
    transactionId: z.string().optional(),
    cancelReason: z.string().optional(),
    salePrice: z.number(),
    installmentAmount: z.number(),
    totalInstallments: z.number(),
    totalInstallmentsPaid: z.number(),
    estimatedRemainingAmount: z.number(),
});
export type LTODefault = z.infer<typeof ltoDefault>;

export const dashboardStats = z.object({
    totalDomains: z.number(),
    totalDomainsLastMonth: z.number(),
    totalDomainsPeriod: z.number(),
    parkingRevenueMTDSummary: z.nullable(z.array(parkingRevenueStatsSummary)),
    parkingRevenueYesterdaySummary: z.nullable(z.array(parkingRevenueStatsSummary)),
    parkingRevenueMTD: z.array(parkingRevenueStatsByDate),
    parkingRevenue: z.array(parkingRevenueStatsByDate),
    parkingProviders: z.array(z.string()),
    portfolioStatsDaily: z.array(portfolioStatsByDate),
    portfolioStatsPeriod: z.array(portfolioStatsByDate),
    parkingStatsLastUpdated: z.optional(z.coerce.date()),
    parkingRevenueDailyAvgPeriod: z.number(),
    parkingRevenueDailyAvgLastMonth: z.number(),
    parkingRevenueDailyAvgCurMonth: z.nullable(z.number()),
    autoPickingRevenue: z.array(autopickingRevenue),
    listingsStats: z.array(listingsStatsByDate),
    listingsStatsFull: z.array(listingsStatsByDate),
    listingsTopSales: z.array(listingActivity),
    listingsTopSalesLTO: z.array(listingActivity),
    listingsRecentPayouts: z.array(listingActivity),
    parkingRevenueSummary: z.nullable(z.array(parkingRevenueStatsSummary)),
    ncAftermarketAuctions: z.array(ncAftermarketAuctionsStats),
    portfolioMetricsStatsDaily: z.array(metricsStatsDaily),
    pickingMetricsStatsDaily: z.array(metricsStatsDaily),
    autorenewStats: z.nullable(autorenewStats),
    portfolioAccountStats: z.nullable(portfolioAccountStats),
    expiringThisMonthStats: z.nullable(expiringThisMonthStats),
    ltoInstallmentsSummary: z.nullable(ltoInstallmentsSummary),
});
export type DashboardStats = z.infer<typeof dashboardStats>;

export const domainAuditEvent = z.object({
    id: z.number(),
    auditEventType: z.string(),
    createdAt: z.coerce.date(),
    eventSource: z.string(),
    eventSourceId: z.string(),
    data: z.string()
});
export type DomainAuditEvent = z.infer<typeof domainAuditEvent>;

export const domainStats = z.object({
    alexaTrafficRank: z.nullable(z.number()),
    appraisedValueCents: z.nullable(z.number()),
    ahrefsDr: z.nullable(z.number()),
    backlinks: z.nullable(z.number()),
    extensionsTaken: z.nullable(z.number()),
    exactSearchVolume: z.nullable(z.number()),
    majesticTrustflow: z.nullable(z.number()),
    semrushAscore: z.nullable(z.number()),
    renewFeeCents: z.nullable(z.number()),
    whoisCreateDate: z.nullable(z.coerce.date()),
    parkingRevenueCents: z.nullable(z.number()),
    parkingUniques: z.nullable(z.number()),
    parkingClicks: z.nullable(z.number()),
});

export type DomainStats = z.infer<typeof domainStats>;

export const domain = z.object({
    domain: z.string(),
    addedAt: z.coerce.date(),
    lastSyncAt: z.coerce.date(),
    status: z.string(),
    whoisGuard: z.boolean(),
    autoRenew: z.boolean(),
    accountId: z.nullable(z.string()),
    createdAt: z.nullable(z.coerce.date()),
    expiresAt: z.nullable(z.coerce.date()),
    customDNS: z.boolean(),
    nameservers: z.array(z.string()),
    source: z.string(),
    pickedAt: z.nullable(z.coerce.date()),
    pickStatus: z.string(),
    stats: z.nullable(domainStats),
    tags: z.optional(z.array(z.string())),
});
export type Domain = z.infer<typeof domain>;

export const inboundEmailAttachment = z.object({
    id: z.number(),
    filename: z.string(),
    fileType: z.string(),
});
export type InboundEmailAttachment = z.infer<typeof inboundEmailAttachment>;

export const inboundEmail = z.object({
    id: z.number(),
    createdAt: z.coerce.date(),
    payload: z.nullable(z.string()),
    sentAt: z.coerce.date(),
    from: z.string(),
    to: z.string(),
    text: z.string(),
    subject: z.string(),
    attachments: z.array(inboundEmailAttachment),
    tags: z.array(z.string()),
    venue: z.optional(z.nullable(z.string())),
    account: z.optional(z.nullable(z.string())),
});
export type InboundEmail = z.infer<typeof inboundEmail>;

export const sendDailySupportEmailJobData = z.object({
    id: z.string(),
    picks: z.array(z.string()).nullish(),
    subject: z.optional(z.string()),
})
export type SendDailySupportEmailJobData = z.infer<typeof sendDailySupportEmailJobData>;

export const job = z.object({
    id: z.number(),
    jobType: z.string(),
    jobId: z.string(),
    status: z.string(),
    startedAt: z.coerce.date(),
    finishedAt: z.nullable(z.coerce.date()),
    jobData: z.nullable(z.string()),
    message: z.nullable(z.string()),
    inboundEmails: z.array(inboundEmail),
});
export type Job = z.infer<typeof job>;


export const domainInfo = z.object({
    domain: domain,
    auditEvents: z.array(domainAuditEvent)
});
export type DomainInfo = z.infer<typeof domainInfo>;

function createCursorPaginatedResponse<ItemType extends z.ZodTypeAny>(itemSchema: ItemType) {
    return z.object({
        next: z.string(),
        previous: z.string(),
        content: z.array(itemSchema)
    });
}

function createPaginatedResponse<ItemType extends z.ZodTypeAny>(itemSchema: ItemType) {
    return z.object({
        total: z.number(),
        totalPages: z.number(),
        currentPage: z.number(),
        pageSize: z.number(),
        content: z.array(itemSchema),
        extra: z.record(z.any()).optional(),
    });
}

export const listTagsResponse = z.object({
    tags: z.array(z.string())
})
export type ListTagsResponse = z.infer<typeof listTagsResponse>;

export const listJobsResponse = createPaginatedResponse(job);
export type ListJobsResponse = z.infer<typeof listJobsResponse>;

export const listPortfolioResponse = createPaginatedResponse(domain);
export type ListPortfolioResponse = z.infer<typeof listPortfolioResponse>;

export const listDomainLeadsResponse = createCursorPaginatedResponse(domain);
export type ListDomainLeadsResponse = z.infer<typeof listDomainLeadsResponse>;


export const getListingsResponse = createPaginatedResponse(listing);
export type GetListingsResponse = z.infer<typeof getListingsResponse>;

export const getListingActivityResponse = createPaginatedResponse(listingActivity);
export type GetListingActivityResponse = z.infer<typeof getListingActivityResponse>;

export const getLTODefaultsResponse = createPaginatedResponse(ltoDefault);
export type GetLTODefaultsResponse = z.infer<typeof getLTODefaultsResponse>;

export interface SetDomainTagsRequest {
    tags: string[];
}

export interface ListDomainLeadsRequest {
    search?: string;
    account?: string;
    pickStatus?: DomainPickStatus[];
    download?: boolean;
    portfolio?: boolean;
    picksToSendOnly?: boolean;
    filterBadNS?: boolean;
    clientHold?: boolean;
    domains?: string[];
    tags?: string[];
    tagAll?: string[];
    needsPricing?: boolean;
}

export interface ListPortfolioRequest {
    search?: string;
}

export enum DomainPickStatus {
    PENDING = 'pending',
    USER_RENEW = 'userRenew',
    UPSTREAM_PICKED = 'upstreamPicked'
}

export enum JobType {
    SendSupportPicksEmail = 'sendSupportPicksEmail',
    SendSupportSpaceshipPicksEmail = 'sendSupportSpaceshipPicksEmail',
    SendInternalAtticRenewalEmail = 'sendInternalAtticRenewalEmail',
    ScanNS = 'scanNS',
    UpdateListings = 'updateListings'
}

export enum JobStatus {
    JobStatusRunning = "running",
	JobStatusFailed  = "failed",
	JobStatusSuccess = "success",
}

export interface ListJobsRequest {
    type?: string[];
    status?: JobStatus;
}

export const activitySold = z.object({
    domain: z.string(),
    price: z.number(),
    timestamp: z.coerce.date(),
    type: transferType,
    payoutType: payoutType,
    installments: z.number().nullish(),
});
export type ActivitySold = z.infer<typeof activitySold>;

export const activityTransferRequest = z.object({
    domain: z.string(),
    timestamp: z.coerce.date(),
    type: transferType,
})
export type ActivityTransferRequest = z.infer<typeof activityTransferRequest>;

export const activityTransferComplete = z.object({
    domain: z.string(),
    timestamp: z.coerce.date(),
    type: transferType,
})
export type ActivityTransferComplete = z.infer<typeof activityTransferComplete>;

export const activityPayout = z.object({
    domain: z.string(),
    timestamp: z.coerce.date(),
    type: payoutType,
    installment: z.number().nullish(),
    totalInstallments: z.number().nullish(),
    amount: z.number(),
    remainingBalance: z.number().nullish(),
})
export type ActivityPayout = z.infer<typeof activityPayout>;

export type GetEmailResponse = z.infer<typeof inboundEmail>;

export const listEmailsResponse = createPaginatedResponse(inboundEmail);
export type ListEmailsResponse = z.infer<typeof listEmailsResponse>;

export interface ListEmailsRequest {
    tag?: string
    search?: string
}

export const getNotificationsResponse = z.object({
    numEmailsReview: z.number(),
    numActiveAlerts: z.number(),
});
export type GetNotificationsResponse = z.infer<typeof getNotificationsResponse>;

export const domainRenewalBlacklist = z.object({
    domain: z.string(),
    addedOn: z.coerce.date(),
});
export type DomainRenewalBlacklist = z.infer<typeof domainRenewalBlacklist>;

export const listDomainRenewalBlacklistResponse = createPaginatedResponse(domainRenewalBlacklist);
export type ListDomainRenewalBlacklistResponse = z.infer<typeof listDomainRenewalBlacklistResponse>;

export interface ListDomainRenewalBlacklistRequest {
    search?: string;
    domains?: string[];
}

export interface UploadSalesHistoryRequest {
    file: File;
    account: string;
    fileType: string;
}

export interface GetListingActivityRequest {
    type?: ListingActivityType;
    start?: Date;
    end?: Date;
    account?: string;
    source?: string;
    domain?: string;
    payoutType?: PayoutType;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
}


export const uploadSalesHistoryResponse = z.object({
    recordsLoaded: z.number(),
});
export type UploadSalesHistoryResponse = z.infer<typeof uploadSalesHistoryResponse>;

// NC Aftermarket live API (proxied vendor responses)
export const ncAftermarketSale = z.object({
    id: z.string().optional(),
    name: z.string().optional(),
    tld: z.string().optional(),
    status: z.string().optional(),
    saleType: z.string().optional(),
    auctionType: z.string().optional(),
    price: z.number().optional(),
    startPrice: z.number().optional(),
    renewPrice: z.number().optional(),
    minBid: z.number().optional(),
    bidCount: z.number().optional(),
    startDate: z.coerce.date().optional(),
    endDate: z.coerce.date().optional(),
    endDateExtendedMs: z.number().optional(),
    createdDate: z.coerce.date().optional(),
    soldDate: z.coerce.date().optional(),
    registeredDate: z.coerce.date().optional(),
    ahrefsDomainRating: z.number().optional(),
    alexaRanking: z.number().optional(),
    umbrellaRanking: z.number().optional(),
    backlinksCount: z.number().optional(),
    cloudflareRanking: z.number().optional(),
    estibotValue: z.number().optional(),
    goDaddyValue: z.number().optional(),
    extensionsTaken: z.number().optional(),
    keywordSearchCount: z.number().optional(),
    keywordSearchQuery: z.string().optional(),
    lastSoldPrice: z.number().optional(),
    lastSoldYear: z.number().nullable().optional(),
});
export type NCAftermarketSale = z.infer<typeof ncAftermarketSale>;

export const listNCAftermarketSalesResponse = z.object({
    nextCursor: z.string().optional().nullable(),
    hasMore: z.boolean().optional(),
    items: z.array(ncAftermarketSale).default([]),
});
export type ListNCAftermarketSalesResponse = z.infer<typeof listNCAftermarketSalesResponse>;

export interface ListNCAftermarketSalesRequest {
    pageSize?: number;
    cursor?: string;
    orderBy?: string;
    direction?: 'asc' | 'desc';
    name?: string;
    tld?: string;
    keywords?: string;
    price?: string;
    nameLength?: string;
    bidCount?: string;
    startDate?: string;
    endDate?: string;
    noHyphens?: boolean;
    noNumbers?: boolean;
    onlyNumbers?: boolean;
}

export const ncAftermarketBid = z.object({
    id: z.string().optional(),
    saleId: z.string().optional(),
    name: z.string().optional(),
    isProxy: z.boolean().optional(),
    amount: z.number().optional(),
    maxAmount: z.number().optional(),
    status: z.string().optional(),
    index: z.number().optional(),
    createdDate: z.coerce.date().optional(),
    isLeadingBid: z.boolean().optional(),
});
export type NCAftermarketBid = z.infer<typeof ncAftermarketBid>;

export const listNCAftermarketBidsResponse = z.object({
    pages: z.object({
        total: z.number().optional(),
        perPage: z.number().optional(),
        page: z.number().optional(),
        lastPage: z.number().optional(),
    }).optional(),
    items: z.array(ncAftermarketBid).default([]),
});
export type ListNCAftermarketBidsResponse = z.infer<typeof listNCAftermarketBidsResponse>;

export interface ListNCAftermarketBidsRequest {
    page?: number;
    pageSize?: number;
    sale?: string;
}

export const uploadManualPicksResponse = z.object({
    unknownDomains: z.array(z.string())
});
export type UploadManualPicksResponse = z.infer<typeof uploadManualPicksResponse>;

export const removePicksResponse = z.object({
    unknownDomains: z.array(z.string())
});
export type RemovePicksResponse = z.infer<typeof removePicksResponse>;

// NC API Accounts
export const ncAPIAccount = z.object({
    id: z.number(),
    username: z.string(),
    token_expires_at: z.nullable(z.coerce.date()),
    last_token_refresh: z.nullable(z.coerce.date()),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
    is_active: z.boolean(),
    notes: z.string().nullable(),
});
export type NCAPIAccount = z.infer<typeof ncAPIAccount>;

export const listNCAPIAccountsResponse = z.object({
    accounts: z.array(ncAPIAccount),
});
export type ListNCAPIAccountsResponse = z.infer<typeof listNCAPIAccountsResponse>;

export const createNCAPIAccountRequest = z.object({
    username: z.string(),
    exchange_token: z.string(),
});
export type CreateNCAPIAccountRequest = z.infer<typeof createNCAPIAccountRequest>;

export const updateNCAPIAccountTokenRequest = z.object({
    exchange_token: z.string(),
});
export type UpdateNCAPIAccountTokenRequest = z.infer<typeof updateNCAPIAccountTokenRequest>;

export const updateNCAPIAccountStatusRequest = z.object({
    is_active: z.boolean(),
});
export type UpdateNCAPIAccountStatusRequest = z.infer<typeof updateNCAPIAccountStatusRequest>;

export const updateNCAPIAccountNotesRequest = z.object({
    notes: z.string(),
});
export type UpdateNCAPIAccountNotesRequest = z.infer<typeof updateNCAPIAccountNotesRequest>;

export const auditEventSearchResult = z.object({
    domain: z.string(),
    auditEventType: z.string(),
    createdAt: z.coerce.date(),
    eventSource: z.string(),
    eventSourceId: z.string(),
    data: z.string(),
});
export type AuditEventSearchResult = z.infer<typeof auditEventSearchResult>;

export const auditEventSearchResponse = createPaginatedResponse(auditEventSearchResult);
export type AuditEventSearchResponse = z.infer<typeof auditEventSearchResponse>;

// Set Domain AutoRenew
export interface SetDomainAutoRenewRequest {
    autoRenew: boolean;
}

export const setDomainAutoRenewResponse = z.object({
    success: z.boolean(),
    autoRenew: z.boolean(),
});
export type SetDomainAutoRenewResponse = z.infer<typeof setDomainAutoRenewResponse>;

// Create Manual Sales Activity
export interface CreateManualSalesActivityRequest {
    domain: string;
    activityType: ListingActivityType;
    venue: string;
    account: string;
    createdAt: string; // RFC3339 format
    salePrice?: number;
    transferType?: TransferType;
    payoutType?: PayoutType;
    installments?: number;
    amount?: number;
    installmentNumber?: number;
    totalInstallments?: number;
    remainingBalance?: number;
    currency?: string;
}

export const createManualSalesActivityResponse = z.object({
    success: z.boolean(),
    message: z.string().optional(),
});
export type CreateManualSalesActivityResponse = z.infer<typeof createManualSalesActivityResponse>;

// Alert Types
export const alertType = z.object({
    id: z.number(),
    slug: z.string(),
    name: z.string(),
    description: z.string(),
    enabled: z.boolean(),
    emailEnabled: z.boolean(),
    emailRecipients: z.string(),
    config: z.any(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export type AlertType = z.infer<typeof alertType>;

export const alert = z.object({
    id: z.number(),
    alertTypeId: z.number(),
    alertKey: z.string(),
    status: z.enum(['active', 'resolved', 'dismissed']),
    notes: z.string(),
    alertData: z.any(),
    triggeredAt: z.coerce.date(),
    dismissedAt: z.nullable(z.coerce.date()),
    dismissedBy: z.string(),
    resolvedAt: z.nullable(z.coerce.date()),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    alertType: z.nullable(alertType).optional(),
});
export type Alert = z.infer<typeof alert>;

export const alertHistory = z.object({
    id: z.number(),
    alertId: z.number(),
    alertData: z.any(),
    createdAt: z.coerce.date(),
});
export type AlertHistory = z.infer<typeof alertHistory>;

export const alertHistoryResponse = z.object({
    history: z.array(alertHistory),
});
export type AlertHistoryResponse = z.infer<typeof alertHistoryResponse>;

export const alertAuditLogEntry = z.object({
    auditEventType: z.string(),
    createdAt: z.coerce.date(),
    eventSource: z.string(),
    data: z.string(),
});
export type AlertAuditLogEntry = z.infer<typeof alertAuditLogEntry>;

export const alertAuditLogResponse = z.object({
    auditLog: z.array(alertAuditLogEntry),
});
export type AlertAuditLogResponse = z.infer<typeof alertAuditLogResponse>;

export const listAlertsResponse = createPaginatedResponse(alert);
export type ListAlertsResponse = z.infer<typeof listAlertsResponse>;

export const listAlertTypesResponse = z.object({
    alertTypes: z.array(alertType),
});
export type ListAlertTypesResponse = z.infer<typeof listAlertTypesResponse>;

export interface ListAlertsRequest {
    status?: string;
    alertTypeId?: number;
    search?: string;
}

export const autoRenewReviewEntry = z.object({
    id: z.number(),
    domain: z.string(),
    queuedAt: z.coerce.date(),
    expiresAt: z.coerce.date(),
    reason: z.string(),
    decisionData: z.any(),
    status: z.enum(['open', 'reviewed_reenabled', 'reviewed_keep_off', 'expired_unreviewed']),
    reviewedAt: z.nullable(z.coerce.date()).optional(),
    reviewedBy: z.string().optional(),
    reviewedAction: z.string().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export type AutoRenewReviewEntry = z.infer<typeof autoRenewReviewEntry>;

export const listAutoRenewReviewResponse = createPaginatedResponse(autoRenewReviewEntry);
export type ListAutoRenewReviewResponse = z.infer<typeof listAutoRenewReviewResponse>;

export interface ListAutoRenewReviewRequest {
    status?: string;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
}

export interface UpdateAlertTypeRequest {
    enabled?: boolean;
    emailEnabled?: boolean;
    emailRecipients?: string;
    config?: any;
}

// NC Auto-Bidder

export const ncAuctionWatchlistItem = z.object({
    id: z.number(),
    domain: z.string(),
    tld: z.string(),
    status: z.string(),
    rejectionReason: z.string().nullable().optional(),
    saleId: z.string().nullable().optional(),
    auctionStartDate: z.coerce.date().nullable().optional(),
    auctionEndDate: z.coerce.date().nullable().optional(),
    minBid: z.number().nullable().optional(),
    currentPrice: z.number().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export type NcAuctionWatchlistItem = z.infer<typeof ncAuctionWatchlistItem>;

export const listWatchlistResponse = z.object({
    total: z.number().optional(),
    currentPage: z.number().optional(),
    totalPages: z.number().optional(),
    pageSize: z.number().optional(),
    content: z.array(ncAuctionWatchlistItem).default([]),
});
export type ListWatchlistResponse = z.infer<typeof listWatchlistResponse>;

export interface ListWatchlistRequest {
    page?: number;
    pageSize?: number;
    status?: string;
    statusNot?: string;
    domain?: string;
}

export const uploadWatchlistResponse = z.object({
    accepted: z.array(z.string()).default([]),
    rejected: z.array(z.object({
        domain: z.string(),
        reason: z.string(),
    })).default([]),
});
export type UploadWatchlistResponse = z.infer<typeof uploadWatchlistResponse>;

export const removeWatchlistResponse = z.object({
    removed: z.array(z.string()).default([]),
    notFound: z.array(z.string()).default([]),
});
export type RemoveWatchlistResponse = z.infer<typeof removeWatchlistResponse>;

export const ncAuctionExcludedTld = z.object({
    id: z.number(),
    tld: z.string(),
    createdAt: z.coerce.date(),
});
export type NcAuctionExcludedTld = z.infer<typeof ncAuctionExcludedTld>;

export const uploadExcludedTldsResponse = z.object({
    added: z.array(z.string()).default([]),
    skipped: z.array(z.string()).default([]),
});
export type UploadExcludedTldsResponse = z.infer<typeof uploadExcludedTldsResponse>;

export const ncAuctionBidConfig = z.object({
    id: z.number(),
    liveMode: z.boolean(),
    enabled: z.boolean(),
    maxBidAmount: z.number(),
    minBidRejectThreshold: z.number(),
    bidWindowMinutes: z.number(),
    bidFloorMinutes: z.number(),
    dailyBudgetCap: z.number().nullable().optional(),
    updatedAt: z.coerce.date(),
    updatedBy: z.string().nullable().optional(),
});
export type NcAuctionBidConfig = z.infer<typeof ncAuctionBidConfig>;

export const ncAuctionBid = z.object({
    id: z.number(),
    watchlistId: z.number(),
    domain: z.string(),
    saleId: z.string(),
    maxAmount: z.number(),
    actualAmount: z.number().nullable().optional(),
    ncBidId: z.string().nullable().optional(),
    status: z.string(),
    dryRun: z.boolean(),
    errorMessage: z.string().nullable().optional(),
    bidLatencyMs: z.number().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export type NcAuctionBid = z.infer<typeof ncAuctionBid>;

export const listAutoBidsResponse = z.object({
    total: z.number().optional(),
    currentPage: z.number().optional(),
    totalPages: z.number().optional(),
    pageSize: z.number().optional(),
    content: z.array(ncAuctionBid).default([]),
});
export type ListAutoBidsResponse = z.infer<typeof listAutoBidsResponse>;

export interface ListAutoBidsRequest {
    page?: number;
    pageSize?: number;
    status?: string;
    domain?: string;
    dryRun?: string;
}

export interface UpdateBidConfigRequest {
    liveMode?: boolean;
    enabled?: boolean;
    maxBidAmount?: number;
    minBidRejectThreshold?: number;
    bidWindowMinutes?: number;
    bidFloorMinutes?: number;
    dailyBudgetCap?: number | null;
}
